// src/components/admin/AdminFeatureConfig.jsx
// Premium Feature Access Control — v3 (compressed rows, rich preview, filter toolbar, hover states)
// GET /api/admin/feature-config  |  POST /api/admin/feature-config

import { useState, useEffect, useCallback, useMemo } from 'react';
import { adminApi } from '../../utils/api.js';
import { toastSuccess, toastError, toastInfo, toastWarning } from '../../utils/toast.js';

/* ════════════════════════════════════════════════
   CONSTANTS
════════════════════════════════════════════════ */
const PLANS = [
  { key: 'free',     label: 'Free',     color: '#8B8578', glow: 'rgba(139,133,120,.4)', bg: 'rgba(139,133,120,.12)', border: 'rgba(139,133,120,.35)' },
  { key: 'paid',     label: 'Pro',      color: '#C9A843', glow: 'rgba(201,168,67,.4)',  bg: 'rgba(201,168,67,.12)',  border: 'rgba(201,168,67,.45)'  },
  { key: 'lifetime', label: 'Lifetime', color: '#3DBE73', glow: 'rgba(61,190,115,.4)',  bg: 'rgba(61,190,115,.12)', border: 'rgba(61,190,115,.4)'   },
];

const GROUPS = [
  { id: 'overview',   title: 'Analytics — Overview',   icon: '◎', desc: 'Core portfolio performance metrics',       keys: ['analytics_overview_drawdown','analytics_overview_heatmap'] },
  { id: 'execution',  title: 'Analytics — Execution',  icon: '⟁', desc: 'Trade execution quality analysis',         keys: ['analytics_execution_strategy','analytics_execution_setup','analytics_execution_pairs'] },
  { id: 'timing',     title: 'Analytics — Timing',     icon: '◷', desc: 'Session and time-of-day performance',      keys: ['analytics_timing_session','analytics_timing_dow','analytics_timing_heatmap'] },
  { id: 'psychology', title: 'Analytics — Psychology', icon: '◈', desc: 'Emotional impact on trade results',        keys: ['analytics_psychology_overview','analytics_psychology_winrate','analytics_psychology_pnl'] },
  { id: 'risk',       title: 'Analytics — Risk',       icon: '⬡', desc: 'Risk management and R:R analysis',         keys: ['analytics_risk_rr','analytics_risk_winloss'] },
  { id: 'pages',      title: 'Platform Pages',          icon: '▣', desc: 'Full page access control',                keys: ['whatif_page'] },
  { id: 'simulator',  title: 'What-If Simulator',       icon: '⟳', desc: 'Scenario planning and projection tools',  keys: ['whatif_filters','whatif_kpis','whatif_curve','whatif_milestones','whatif_compound','whatif_setups','whatif_heatmap','whatif_sizing'] },
  { id: 'aicoach',    title: 'AI Coach',                 icon: '◈', desc: 'AI coaching report premium features',     keys: ['ai_recovery_plan','ai_benchmarks','ai_whatif_scenarios','ai_insights_full','ai_unlimited_reports','ai_dna_profile'] },
];

const FEATURES = {
  analytics_overview_drawdown:   { label: 'Equity Curve & Drawdown',   desc: 'Visual equity progression with max drawdown overlay',         group: 'Analytics' },
  analytics_overview_heatmap:    { label: 'Monthly P&L Heatmap',       desc: 'Calendar-view monthly profit and loss distribution',          group: 'Analytics' },
  analytics_execution_strategy:  { label: 'Strategy P&L Breakdown',    desc: 'Per-strategy performance comparison and attribution',          group: 'Analytics' },
  analytics_execution_setup:     { label: 'Setup Quality by Strategy', desc: 'Win rate and expectancy grouped by trade setup type',          group: 'Analytics' },
  analytics_execution_pairs:     { label: 'Performance by Pair',       desc: 'Currency pair breakdown with win/loss ratios',                group: 'Analytics' },
  analytics_timing_session:      { label: 'Performance by Session',    desc: 'London, New York, Tokyo session performance split',           group: 'Analytics' },
  analytics_timing_dow:          { label: 'Best Day of Week',          desc: 'Day-of-week profitability ranking and patterns',              group: 'Analytics' },
  analytics_timing_heatmap:      { label: 'Time × Day Heatmap',        desc: 'Hour and weekday correlation heatmap matrix',                 group: 'Analytics' },
  analytics_psychology_overview: { label: 'Emotion vs. Performance',   desc: 'Logged emotional state impact on trade outcomes',             group: 'Psychology' },
  analytics_psychology_winrate:  { label: 'Emotion Win Rate Ranking',  desc: 'Win rate comparison across emotional states',                 group: 'Psychology' },
  analytics_psychology_pnl:      { label: 'Emotion P&L Attribution',   desc: 'P&L contribution segmented by emotional tag',                group: 'Psychology' },
  analytics_risk_rr:             { label: 'Risk & R:R Breakdown',      desc: 'Risk-reward distribution and target achievement',             group: 'Risk' },
  analytics_risk_winloss:        { label: 'Win vs. Loss Size',         desc: 'Average win vs average loss magnitude comparison',            group: 'Risk' },
  whatif_page:                   { label: 'What-If Simulator',         desc: 'Full scenario simulation page access',                        group: 'Simulator' },
  whatif_filters:                { label: 'Simulator Filters',         desc: 'Advanced parameter filtering for simulations',                group: 'Simulator' },
  whatif_kpis:                   { label: 'KPI Cards',                 desc: 'Key performance indicator summary cards',                     group: 'Simulator' },
  whatif_curve:                  { label: 'Equity Curve',              desc: 'Projected equity curve under simulated conditions',           group: 'Simulator' },
  whatif_milestones:             { label: 'Milestones',                desc: 'Target milestone tracking and progress',                      group: 'Simulator' },
  whatif_compound:               { label: 'Compound Projector',        desc: 'Compound growth projection over time',                        group: 'Simulator' },
  whatif_setups:                 { label: 'Best Setup Finder',         desc: 'AI-ranked optimal trade setup recommendations',              group: 'Simulator' },
  whatif_heatmap:                { label: 'Timing Heatmap',            desc: 'Optimal entry timing heatmap visualization',                 group: 'Simulator' },
  whatif_sizing:                 { label: 'Position Sizing Impact',    desc: 'Position size sensitivity and risk impact analysis',          group: 'Simulator' },

  ai_recovery_plan:               { label: 'Profit Leak Recovery Plan',  desc: 'Full recovery plan for the top detected profit leak',         group: 'AI Coach' },
  ai_benchmarks:                  { label: 'Full Performance Benchmarks', desc: 'Detailed peer benchmark comparisons',                        group: 'AI Coach' },
  ai_whatif_scenarios:            { label: 'Full What-If Scenarios',     desc: 'All AI-generated what-if scenarios, not just the first two',  group: 'AI Coach' },
  ai_insights_full:                { label: 'Full Insights List',        desc: 'Unlimited AI insights beyond the first four',                  group: 'AI Coach' },
  ai_unlimited_reports:           { label: 'Unlimited AI Reports',       desc: 'Generate AI coaching reports without the cooldown wait',       group: 'AI Coach' },
  ai_dna_profile:                  { label: 'Trading DNA Profile',        desc: 'Trader type, strengths/weaknesses, and performance forecast', group: 'AI Coach' },
};

const DEFAULT_CONFIG = Object.fromEntries(
  Object.keys(FEATURES).map(k => [k, { plans: ['free','paid','lifetime'] }])
);

const VISIBILITY_FILTERS = [
  { key: 'all',      label: 'All'      },
  { key: 'open',     label: 'Open'     },
  { key: 'pro',      label: 'Pro+'     },
  { key: 'locked',   label: 'Locked'   },
];

/* ════════════════════════════════════════════════
   UTILS
════════════════════════════════════════════════ */
function getPlanStatus(plans = []) {
  if (!plans.length) return 'locked';
  if (PLANS.every(p => plans.includes(p.key))) return 'open';
  if (plans.includes('lifetime') && !plans.includes('free')) return 'premium';
  if (plans.includes('paid') && !plans.includes('free')) return 'pro';
  return 'partial';
}

function matchesVisibility(plans, filter) {
  if (filter === 'all') return true;
  const s = getPlanStatus(plans);
  if (filter === 'open') return s === 'open';
  if (filter === 'pro') return s === 'pro' || s === 'premium' || s === 'partial';
  if (filter === 'locked') return s === 'locked';
  return true;
}

function useIsMobile() {
  const [v, setV] = useState(() => window.innerWidth < 900);
  useEffect(() => {
    const fn = () => setV(window.innerWidth < 900);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);
  return v;
}

function timeAgo(ts) {
  if (!ts) return null;
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
}

/* ════════════════════════════════════════════════
   STATUS CHIP
════════════════════════════════════════════════ */
function StatusChip({ plans }) {
  const s = getPlanStatus(plans);
  const map = {
    open:    { label: 'All Plans',     color: '#3DBE73', bg: 'rgba(61,190,115,.1)',  border: 'rgba(61,190,115,.25)'  },
    pro:     { label: 'Pro+',          color: '#C9A843', bg: 'rgba(201,168,67,.1)',  border: 'rgba(201,168,67,.28)'  },
    premium: { label: 'Lifetime',      color: '#3DBE73', bg: 'rgba(61,190,115,.08)', border: 'rgba(61,190,115,.2)'   },
    partial: { label: 'Custom',        color: '#C9A843', bg: 'rgba(201,168,67,.08)', border: 'rgba(201,168,67,.22)'  },
    locked:  { label: 'Locked',        color: '#D94F4F', bg: 'rgba(217,79,79,.08)',  border: 'rgba(217,79,79,.22)'   },
  };
  const c = map[s] || map.locked;
  return (
    <span style={{
      fontSize: 9, fontWeight: 700, letterSpacing: '.09em', textTransform: 'uppercase',
      padding: '2px 7px', borderRadius: 4, whiteSpace: 'nowrap', flexShrink: 0,
      color: c.color, background: c.bg, border: `1px solid ${c.border}`,
    }}>{c.label}</span>
  );
}

/* ════════════════════════════════════════════════
   PLAN SEGMENT — compact, inline
════════════════════════════════════════════════ */
function PlanSegment({ activePlans, onChange }) {
  return (
    <div style={{
      display: 'inline-flex', borderRadius: 6, overflow: 'hidden',
      border: '1px solid rgba(255,255,255,.07)', background: 'rgba(0,0,0,.25)',
      flexShrink: 0,
    }}>
      {PLANS.map((plan, i) => {
        const on = activePlans.includes(plan.key);
        return (
          <button
            key={plan.key}
            onClick={(e) => { e.stopPropagation(); onChange(plan.key); }}
            title={`Toggle ${plan.label}`}
            style={{
              display: 'flex', alignItems: 'center', gap: 4,
              padding: '4px 10px',
              borderRight: i < PLANS.length - 1 ? '1px solid rgba(255,255,255,.06)' : 'none',
              background: on ? plan.bg : 'transparent',
              color: on ? plan.color : 'rgba(200,192,168,.2)',
              fontSize: 10, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase',
              cursor: 'pointer', fontFamily: 'inherit', transition: 'all .12s', whiteSpace: 'nowrap',
            }}
          >
            <span style={{
              width: 5, height: 5, borderRadius: '50%', flexShrink: 0,
              background: on ? plan.color : 'rgba(255,255,255,.1)',
              boxShadow: on ? `0 0 4px ${plan.glow}` : 'none',
              transition: 'all .12s',
            }} />
            {plan.label}
          </button>
        );
      })}
    </div>
  );
}

/* ════════════════════════════════════════════════
   METRIC CARD — with progress bar + percentage
════════════════════════════════════════════════ */
function MetricCard({ icon, value, total, label, sub, color, bg, border }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div style={{
      background: bg, border: `1px solid ${border}`, borderRadius: 8,
      padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <div>
          <div style={{ fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(200,192,168,.3)', fontWeight: 700, marginBottom: 4 }}>{label}</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 22, fontWeight: 500, color, lineHeight: 1 }}>{value}</span>
            {total > 0 && (
              <span style={{ fontSize: 10, color: 'rgba(200,192,168,.3)', fontFamily: "'DM Mono', monospace" }}>
                {pct}%
              </span>
            )}
          </div>
        </div>
        <span style={{ fontSize: 15, opacity: .5, marginTop: 2 }}>{icon}</span>
      </div>
      {/* Progress bar */}
      <div style={{ height: 2, background: 'rgba(255,255,255,.06)', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{
          height: '100%', width: `${pct}%`, borderRadius: 2,
          background: `linear-gradient(90deg, ${color}, ${color}88)`,
          transition: 'width .4s ease',
        }} />
      </div>
      <div style={{ fontSize: 10, color: 'rgba(200,192,168,.25)', lineHeight: 1.4 }}>{sub}</div>
    </div>
  );
}

/* ════════════════════════════════════════════════
   COMPACT FEATURE ROW — ~35% shorter than v2
════════════════════════════════════════════════ */
function FeatureRow({ featureKey, feat, activePlans, onToggle, onPreview, isSelected, onSelect }) {
  const [hovered, setHovered] = useState(false);
  const status = getPlanStatus(activePlans);
  const dotColor = { open: '#3DBE73', pro: '#C9A843', premium: '#3DBE73', partial: '#C9A843', locked: '#D94F4F' }[status];

  return (
    <div
      onMouseEnter={() => { setHovered(true); onPreview(featureKey); }}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'grid',
        gridTemplateColumns: '18px 1fr auto auto',
        alignItems: 'center',
        gap: 10,
        padding: '9px 12px',
        borderRadius: 7,
        background: isSelected ? 'rgba(201,168,67,.05)' : hovered ? '#16161c' : '#111115',
        border: `1px solid ${isSelected ? 'rgba(201,168,67,.3)' : hovered ? 'rgba(255,255,255,.13)' : 'rgba(255,255,255,.055)'}`,
        boxShadow: hovered ? '0 2px 16px rgba(0,0,0,.35), 0 0 0 1px rgba(255,255,255,.04)' : 'none',
        transition: 'all .14s ease',
        cursor: 'default',
      }}
    >
      {/* Checkbox */}
      <div
        onClick={() => onSelect(featureKey)}
        style={{
          width: 14, height: 14, borderRadius: 3, cursor: 'pointer',
          border: `1px solid ${isSelected ? 'rgba(201,168,67,.6)' : hovered ? 'rgba(255,255,255,.2)' : 'rgba(255,255,255,.1)'}`,
          background: isSelected ? 'rgba(201,168,67,.18)' : 'transparent',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all .12s', flexShrink: 0,
        }}
      >
        {isSelected && <span style={{ fontSize: 8, color: '#C9A843', fontWeight: 900, lineHeight: 1 }}>✓</span>}
      </div>

      {/* Label + desc in one compact line */}
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{
            width: 5, height: 5, borderRadius: '50%', flexShrink: 0,
            background: dotColor,
            boxShadow: status !== 'locked' ? `0 0 4px ${dotColor}` : 'none',
            transition: 'all .2s',
          }} />
          <span style={{
            fontSize: 12, fontWeight: 600,
            color: hovered ? 'rgba(200,192,168,1)' : 'rgba(200,192,168,.85)',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            transition: 'color .14s',
          }}>
            {feat.label}
          </span>
          <span style={{
            fontSize: 10, color: 'rgba(200,192,168,.28)',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            flexShrink: 1,
          }}>
            — {feat.desc}
          </span>
        </div>
      </div>

      {/* Plan segment */}
      <PlanSegment activePlans={activePlans} onChange={(pk) => onToggle(featureKey, pk)} />

      {/* Status chip */}
      <StatusChip plans={activePlans} />
    </div>
  );
}

/* ════════════════════════════════════════════════
   PREVIEW PANEL — rich locked-state mockup
════════════════════════════════════════════════ */
function PreviewPanel({ featureKey, config }) {
  const feat   = featureKey ? FEATURES[featureKey] : null;
  const plans  = featureKey ? (config[featureKey]?.plans ?? []) : [];
  const status = getPlanStatus(plans);

  const accessRows = PLANS.map(p => ({
    ...p, canSee: plans.includes(p.key),
  }));

  // Which plan is the minimum required?
  const minPlan = plans.includes('free') ? null
    : plans.includes('paid') ? 'Pro'
    : plans.includes('lifetime') ? 'Lifetime'
    : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Header */}
      <div style={{ paddingBottom: 10, borderBottom: '1px solid rgba(255,255,255,.06)' }}>
        <div style={{ fontSize: 9, letterSpacing: '.15em', textTransform: 'uppercase', color: 'rgba(201,168,67,.45)', fontWeight: 700, marginBottom: 5 }}>
          Live Preview
        </div>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'rgba(200,192,168,.7)' }}>
          What User Sees
        </div>
      </div>

      {feat ? (
        <>
          {/* Mocked widget card */}
          <div style={{
            background: 'rgba(255,255,255,.025)', border: '1px solid rgba(255,255,255,.07)',
            borderRadius: 8, overflow: 'hidden',
          }}>
            {/* Widget top bar */}
            <div style={{
              padding: '10px 12px 8px',
              borderBottom: '1px solid rgba(255,255,255,.05)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: 'rgba(200,192,168,.6)' }}>{feat.label}</span>
              <span style={{ fontSize: 9, color: 'rgba(200,192,168,.25)', fontFamily: "'DM Mono', monospace" }}>widget</span>
            </div>

            {/* Locked state */}
            {status === 'locked' ? (
              <div style={{
                padding: '24px 16px',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, textAlign: 'center',
              }}>
                <div style={{
                  width: 36, height: 36, borderRadius: '50%',
                  background: 'rgba(217,79,79,.1)', border: '1px solid rgba(217,79,79,.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16,
                }}>🔒</div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(200,192,168,.5)' }}>Locked Feature</div>
                <div style={{ fontSize: 10, color: 'rgba(200,192,168,.25)', lineHeight: 1.5 }}>
                  No plan currently has access to this feature.
                </div>
              </div>
            ) : minPlan ? (
              /* Upgrade prompt state */
              <div style={{
                padding: '20px 14px',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, textAlign: 'center',
              }}>
                <div style={{
                  width: 34, height: 34, borderRadius: '50%',
                  background: 'rgba(201,168,67,.08)', border: '1px solid rgba(201,168,67,.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15,
                }}>🔒</div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(201,168,67,.7)', marginBottom: 4 }}>
                    {minPlan} Feature
                  </div>
                  <div style={{ fontSize: 10, color: 'rgba(200,192,168,.3)', lineHeight: 1.5 }}>
                    Upgrade to {minPlan} to unlock {feat.label.toLowerCase()}.
                  </div>
                </div>
                {/* Blurred preview bars */}
                <div style={{
                  width: '100%', padding: '8px', borderRadius: 5,
                  background: 'rgba(255,255,255,.025)',
                  filter: 'blur(3px)', pointerEvents: 'none',
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 28 }}>
                    {[50,70,40,80,60,90,35,65].map((h, i) => (
                      <div key={i} style={{
                        flex: 1, height: `${h}%`, borderRadius: '2px 2px 0 0',
                        background: `rgba(201,168,67,${.15 + i * .03})`,
                      }} />
                    ))}
                  </div>
                </div>
                <div style={{
                  padding: '6px 14px', borderRadius: 20, fontSize: 9,
                  fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase',
                  background: 'rgba(201,168,67,.1)', border: '1px solid rgba(201,168,67,.3)', color: '#C9A843',
                }}>
                  Upgrade to {minPlan} →
                </div>
              </div>
            ) : (
              /* Accessible state */
              <div style={{ padding: '10px 12px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 44 }}>
                  {[50,70,40,80,60,90,35,65,75,55].map((h, i) => (
                    <div key={i} style={{
                      flex: 1, height: `${h}%`, borderRadius: '2px 2px 0 0',
                      background: `rgba(61,190,115,${.15 + i * .03})`,
                    }} />
                  ))}
                </div>
                <div style={{ marginTop: 8, fontSize: 9, color: 'rgba(61,190,115,.5)', fontWeight: 600, letterSpacing: '.06em' }}>
                  ✓ Visible to users
                </div>
              </div>
            )}
          </div>

          {/* Access by plan */}
          <div>
            <div style={{ fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(200,192,168,.22)', fontWeight: 700, marginBottom: 7 }}>
              Access by plan
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {accessRows.map(row => (
                <div key={row.key} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '6px 9px', borderRadius: 5,
                  background: row.canSee ? row.bg : 'rgba(255,255,255,.02)',
                  border: `1px solid ${row.canSee ? row.border : 'rgba(255,255,255,.04)'}`,
                  transition: 'all .2s',
                }}>
                  <span style={{
                    width: 5, height: 5, borderRadius: '50%', flexShrink: 0,
                    background: row.canSee ? row.color : 'rgba(255,255,255,.12)',
                    boxShadow: row.canSee ? `0 0 4px ${row.glow}` : 'none',
                  }} />
                  <span style={{ fontSize: 10, fontWeight: 600, flex: 1, color: row.canSee ? row.color : 'rgba(200,192,168,.22)' }}>
                    {row.label}
                  </span>
                  <span style={{ fontSize: 9, fontWeight: 700, color: row.canSee ? row.color : 'rgba(200,192,168,.18)', letterSpacing: '.05em' }}>
                    {row.canSee ? '✓ Visible' : '✕ Hidden'}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Feature note */}
          <div style={{
            padding: '8px 10px', borderRadius: 5,
            background: 'rgba(255,255,255,.02)', border: '1px solid rgba(255,255,255,.04)',
          }}>
            <div style={{ fontSize: 10, color: 'rgba(200,192,168,.25)', lineHeight: 1.5 }}>{feat.desc}</div>
          </div>
        </>
      ) : (
        <div style={{ padding: '32px 0', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, opacity: .35 }}>
          <span style={{ fontSize: 24 }}>◎</span>
          <span style={{ fontSize: 11, color: 'rgba(200,192,168,.5)', lineHeight: 1.5 }}>
            Hover any feature<br/>to preview user experience
          </span>
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════
   CATEGORY SECTION
════════════════════════════════════════════════ */
function CategorySection({ group, config, selectedKeys, onToggle, onSelect, onPreview, onBulkSet, visFilter }) {
  const [collapsed, setCollapsed] = useState(false);
  const visKeys    = group.keys.filter(k => matchesVisibility(config[k]?.plans ?? [], visFilter));
  const allSel     = visKeys.length > 0 && visKeys.every(k => selectedKeys.has(k));
  const someSel    = visKeys.some(k => selectedKeys.has(k));

  if (visKeys.length === 0) return null;

  return (
    <div style={{ marginBottom: 6 }}>
      {/* Sticky group header */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 10,
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '8px 0 6px',
        background: 'linear-gradient(180deg, #0b0b0d 75%, transparent)',
      }}>
        {/* Group checkbox */}
        <div
          onClick={() => visKeys.forEach(k => onSelect(k, !allSel))}
          style={{
            width: 14, height: 14, borderRadius: 3, flexShrink: 0,
            border: `1px solid ${someSel ? 'rgba(201,168,67,.5)' : 'rgba(255,255,255,.1)'}`,
            background: allSel ? 'rgba(201,168,67,.15)' : someSel ? 'rgba(201,168,67,.06)' : 'transparent',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all .12s',
          }}
        >
          {allSel && <span style={{ fontSize: 8, color: '#C9A843', fontWeight: 900 }}>✓</span>}
          {!allSel && someSel && <span style={{ fontSize: 10, color: '#C9A843', lineHeight: 1 }}>−</span>}
        </div>

        <span style={{ fontSize: 11, opacity: .35 }}>{group.icon}</span>

        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: 'rgba(200,192,168,.45)' }}>
          {group.title}
        </span>
        <span style={{
          fontSize: 9, fontFamily: "'DM Mono', monospace",
          background: 'rgba(201,168,67,.07)', color: 'rgba(201,168,67,.45)',
          padding: '1px 5px', borderRadius: 3,
        }}>{visKeys.length}</span>

        <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,.05)' }} />

        {/* Group bulk quick-set */}
        <div style={{ display: 'flex', gap: 3 }}>
          {[
            { label: 'All',  plans: ['free','paid','lifetime'], color: 'rgba(200,192,168,.28)' },
            { label: 'Pro+', plans: ['paid','lifetime'],        color: 'rgba(201,168,67,.4)'   },
            { label: 'LTD',  plans: ['lifetime'],               color: 'rgba(61,190,115,.4)'   },
          ].map(a => (
            <button key={a.label} onClick={() => onBulkSet(group.keys, a.plans, group.title)} style={{
              padding: '2px 7px', background: 'transparent',
              border: `1px solid ${a.color}`, borderRadius: 4,
              color: a.color, fontSize: 8, fontWeight: 700, letterSpacing: '.06em',
              textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'inherit', transition: 'all .15s',
            }}>{a.label}</button>
          ))}
        </div>

        {/* Collapse */}
        <button onClick={() => setCollapsed(v => !v)} style={{
          background: 'transparent', border: 'none', cursor: 'pointer',
          color: 'rgba(200,192,168,.2)', fontSize: 11, padding: '0 2px',
          transition: 'transform .2s', transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
          fontFamily: 'inherit', lineHeight: 1,
        }}>▾</button>
      </div>

      {/* Rows */}
      {!collapsed && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 16 }}>
          {visKeys.map(key => (
            <FeatureRow
              key={key}
              featureKey={key}
              feat={FEATURES[key]}
              activePlans={config[key]?.plans ?? []}
              onToggle={onToggle}
              onPreview={onPreview}
              isSelected={selectedKeys.has(key)}
              onSelect={(k) => onSelect(k, !selectedKeys.has(k))}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════
   BULK TOOLBAR
════════════════════════════════════════════════ */
function BulkToolbar({ selectedKeys, onSetSelected, onClearSelection }) {
  const count = selectedKeys.size;
  if (count === 0) return null;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '8px 12px', marginBottom: 10,
      background: 'rgba(201,168,67,.05)', border: '1px solid rgba(201,168,67,.18)',
      borderRadius: 7, flexWrap: 'wrap',
    }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: 'rgba(201,168,67,.75)', whiteSpace: 'nowrap' }}>
        {count} selected
      </span>
      <div style={{ width: 1, height: 12, background: 'rgba(255,255,255,.1)' }} />
      {[
        { label: 'All open',    plans: ['free','paid','lifetime'], color: 'rgba(200,192,168,.5)' },
        { label: 'Set Pro+',    plans: ['paid','lifetime'],        color: 'rgba(201,168,67,.7)'  },
        { label: 'Set LTD',     plans: ['lifetime'],               color: 'rgba(61,190,115,.7)'  },
        { label: 'Lock all',    plans: [],                         color: 'rgba(217,79,79,.7)'   },
      ].map(a => (
        <button key={a.label} onClick={() => onSetSelected(a.plans)} style={{
          padding: '3px 9px', background: 'transparent',
          border: `1px solid ${a.color}40`, borderRadius: 4,
          color: a.color, fontSize: 9, fontWeight: 700, letterSpacing: '.05em',
          textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'inherit', transition: 'all .15s',
        }}>{a.label}</button>
      ))}
      <button onClick={onClearSelection} style={{
        marginLeft: 'auto', padding: '3px 9px', background: 'transparent',
        border: '1px solid rgba(255,255,255,.07)', borderRadius: 4,
        color: 'rgba(200,192,168,.3)', fontSize: 9, fontWeight: 600,
        cursor: 'pointer', fontFamily: 'inherit',
      }}>✕ Clear</button>
    </div>
  );
}

/* ════════════════════════════════════════════════
   MAIN COMPONENT
════════════════════════════════════════════════ */
export default function AdminFeatureConfig() {
  const isMobile = useIsMobile();

  const [config,       setConfig]       = useState(null);
  const [savedConfig,  setSavedConfig]  = useState(null);
  const [saving,       setSaving]       = useState(false);
  const [isDirty,      setIsDirty]      = useState(false);
  const [lastSaved,    setLastSaved]    = useState(null);
  const [query,        setQuery]        = useState('');
  const [visFilter,    setVisFilter]    = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [previewKey,   setPreviewKey]   = useState(null);
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [tick,         setTick]         = useState(0);

  useEffect(() => {
    const t = setInterval(() => setTick(n => n + 1), 30000);
    return () => clearInterval(t);
  }, []);

  /* Load */
  useEffect(() => {
    adminApi.getFeatureConfig()
      .then(data => {
        const merged = { ...DEFAULT_CONFIG, ...data };
        setConfig(merged);
        setSavedConfig(JSON.parse(JSON.stringify(merged)));
      })
      .catch(() => {
        const def = JSON.parse(JSON.stringify(DEFAULT_CONFIG));
        setConfig(def);
        setSavedConfig(JSON.parse(JSON.stringify(def)));
      });
  }, []);

  /* Unsaved count */
  const unsavedCount = useMemo(() => {
    if (!config || !savedConfig) return 0;
    return Object.keys(config).filter(k => {
      const a = [...(config[k]?.plans || [])].sort().join(',');
      const b = [...(savedConfig[k]?.plans || [])].sort().join(',');
      return a !== b;
    }).length;
  }, [config, savedConfig]);

  /* Stats */
  const allKeys = Object.keys(FEATURES);
  const stats = useMemo(() => {
    if (!config) return { total: 0, open: 0, proOnly: 0, locked: 0 };
    return {
      total:   allKeys.length,
      open:    allKeys.filter(k => getPlanStatus(config[k]?.plans) === 'open').length,
      proOnly: allKeys.filter(k => { const s = getPlanStatus(config[k]?.plans); return s === 'pro' || s === 'premium' || s === 'partial'; }).length,
      locked:  allKeys.filter(k => getPlanStatus(config[k]?.plans) === 'locked').length,
    };
  }, [config]);

  /* Toggle plan */
  const togglePlan = useCallback((key, planKey) => {
    setConfig(prev => {
      const cur  = prev[key]?.plans ?? [];
      const was  = cur.includes(planKey);
      const next = was ? cur.filter(p => p !== planKey) : [...cur, planKey];
      const planLabel = PLANS.find(p => p.key === planKey)?.label;
      if (was) toastWarning(`${planLabel} removed from "${FEATURES[key]?.label}"`, 3000);
      else     toastSuccess(`${planLabel} granted to "${FEATURES[key]?.label}"`, 3000);
      return { ...prev, [key]: { ...prev[key], plans: next } };
    });
    setIsDirty(true);
  }, []);

  /* Bulk set selected */
  const handleSetSelected = useCallback((plans) => {
    setConfig(prev => {
      const next = { ...prev };
      selectedKeys.forEach(k => { next[k] = { ...next[k], plans }; });
      return next;
    });
    setIsDirty(true);
    const label = plans.length === 3 ? 'All plans' : plans.length === 2 ? 'Pro+' : plans.length === 1 ? 'Lifetime' : 'Locked';
    toastInfo(`${selectedKeys.size} features → ${label}`, 3000);
    setSelectedKeys(new Set());
  }, [selectedKeys]);

  /* Bulk set group */
  const handleBulkSet = useCallback((keys, plans, title) => {
    setConfig(prev => {
      const next = { ...prev };
      keys.forEach(k => { next[k] = { ...next[k], plans }; });
      return next;
    });
    setIsDirty(true);
    const desc = plans.length === 3 ? 'All open' : plans.length === 2 ? 'Pro+ only' : 'LTD only';
    toastInfo(`${title}: ${desc}`, 3000);
  }, []);

  /* Select */
  const handleSelect = useCallback((key, val) => {
    setSelectedKeys(prev => {
      const next = new Set(prev);
      val ? next.add(key) : next.delete(key);
      return next;
    });
  }, []);

  /* Reset */
  const handleReset = useCallback(() => {
    if (!savedConfig) return;
    setConfig(JSON.parse(JSON.stringify(savedConfig)));
    setIsDirty(false);
    setSelectedKeys(new Set());
    toastInfo('Changes discarded', 3000);
  }, [savedConfig]);

  /* Save */
  const handleSave = async () => {
    setSaving(true);
    try {
      await adminApi.saveFeatureConfig(config);
      setSavedConfig(JSON.parse(JSON.stringify(config)));
      setIsDirty(false);
      setLastSaved(Date.now());
      toastSuccess('Configuration saved', 4000);
    } catch {
      toastError('Save failed — please retry', 5000);
    } finally {
      setSaving(false);
    }
  };

  /* Filtered groups */
  const filteredGroups = useMemo(() => {
    if (!config) return [];
    const q = query.toLowerCase();
    return GROUPS
      .filter(g => categoryFilter === 'all' || g.id === categoryFilter)
      .map(g => ({
        ...g,
        keys: g.keys.filter(k => {
          const f = FEATURES[k];
          const matchQ  = !q || f.label.toLowerCase().includes(q) || f.desc.toLowerCase().includes(q) || k.includes(q);
          const matchV  = matchesVisibility(config[k]?.plans ?? [], visFilter);
          return matchQ && matchV;
        }),
      }))
      .filter(g => g.keys.length > 0);
  }, [config, query, visFilter, categoryFilter]);

  if (!config) return (
    <div style={{ padding: 60, color: 'rgba(200,192,168,.2)', fontSize: 13, fontFamily: "'DM Sans', sans-serif", textAlign: 'center' }}>
      Loading configuration…
    </div>
  );

  /* ── Save bar height so it doesn't overlap sidebar content ── */
  const SAVE_BAR_H = 52;

  return (
    <div style={{
      fontFamily: "'DM Sans', sans-serif",
      display: 'flex', flexDirection: 'column',
      /* Critical: fill only the main-col content area, never the sidebar */
      height: '100%', overflow: 'hidden',
    }}>

      {/* ── HEADER ── */}
      <div style={{
        padding: isMobile ? '16px 16px 12px' : '20px 24px 16px',
        borderBottom: '1px solid rgba(255,255,255,.06)',
        background: '#0b0b0d', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5 }}>
              <div style={{ width: 14, height: 1, background: 'rgba(201,168,67,.5)' }} />
              <span style={{ fontSize: 9, letterSpacing: '.2em', textTransform: 'uppercase', color: 'rgba(201,168,67,.5)', fontWeight: 700 }}>Admin Console</span>
            </div>
            <h1 style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: isMobile ? 24 : 30, letterSpacing: '.05em',
              color: 'rgba(200,192,168,.95)', margin: 0, lineHeight: 1, marginBottom: 4,
            }}>
              Feature Access Control
            </h1>
            <p style={{ fontSize: 11, color: 'rgba(200,192,168,.3)', margin: 0 }}>
              Manage which subscription plans can access platform features
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {unsavedCount > 0 && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '4px 10px', borderRadius: 5,
                background: 'rgba(201,168,67,.08)', border: '1px solid rgba(201,168,67,.22)',
              }}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#C9A843', boxShadow: '0 0 5px rgba(201,168,67,.6)' }} />
                <span style={{ fontSize: 11, fontWeight: 700, color: '#C9A843' }}>{unsavedCount} unsaved</span>
              </div>
            )}
            {lastSaved && (
              <span style={{ fontSize: 10, color: 'rgba(200,192,168,.22)' }}>
                Saved {timeAgo(lastSaved)}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── STATS — compact 4-col ── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)',
        gap: 8, padding: isMobile ? '10px 16px' : '12px 24px',
        borderBottom: '1px solid rgba(255,255,255,.05)',
        background: '#0b0b0d', flexShrink: 0,
      }}>
        <MetricCard icon="⊞" value={stats.total}   total={stats.total} label="Total Features" sub="Across all groups"       color="rgba(200,192,168,.6)" bg="rgba(255,255,255,.02)" border="rgba(255,255,255,.07)" />
        <MetricCard icon="◯" value={stats.open}     total={stats.total} label="Open to All"    sub="Free + Pro + Lifetime"   color="#3DBE73"              bg="rgba(61,190,115,.04)"  border="rgba(61,190,115,.12)" />
        <MetricCard icon="◆" value={stats.proOnly}  total={stats.total} label="Pro Exclusive"  sub="Gated from free users"   color="#C9A843"              bg="rgba(201,168,67,.04)"  border="rgba(201,168,67,.12)" />
        <MetricCard icon="★" value={stats.locked}   total={stats.total} label="Locked"         sub="No plan has access"      color="#D94F4F"              bg="rgba(217,79,79,.04)"   border="rgba(217,79,79,.12)"  />
      </div>

      {/* ── FILTER TOOLBAR — search + category + visibility ── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        padding: isMobile ? '8px 16px' : '9px 24px',
        borderBottom: '1px solid rgba(255,255,255,.05)',
        background: '#0b0b0d', flexShrink: 0,
      }}>
        {/* Search */}
        <div style={{ position: 'relative', width: isMobile ? '100%' : 220 }}>
          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'rgba(200,192,168,.2)', fontSize: 12, pointerEvents: 'none' }}>⌕</span>
          <input
            type="text" value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search features…"
            style={{
              width: '100%', padding: '6px 10px 6px 28px',
              background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.07)',
              borderRadius: 6, color: 'rgba(200,192,168,.8)',
              fontFamily: 'inherit', fontSize: 11, outline: 'none', transition: 'border-color .15s',
            }}
            onFocus={e => { e.target.style.borderColor = 'rgba(201,168,67,.3)'; }}
            onBlur={e  => { e.target.style.borderColor = 'rgba(255,255,255,.07)'; }}
          />
        </div>

        {/* Category filter */}
        <select
          value={categoryFilter}
          onChange={e => setCategoryFilter(e.target.value)}
          style={{
            padding: '5px 10px', background: 'rgba(255,255,255,.04)',
            border: '1px solid rgba(255,255,255,.07)', borderRadius: 6,
            color: 'rgba(200,192,168,.7)', fontFamily: 'inherit', fontSize: 11,
            outline: 'none', cursor: 'pointer',
          }}
        >
          <option value="all" style={{ background: '#111' }}>All Categories</option>
          {GROUPS.map(g => (
            <option key={g.id} value={g.id} style={{ background: '#111' }}>{g.title}</option>
          ))}
        </select>

        {/* Visibility filter pills */}
        <div style={{ display: 'flex', gap: 3 }}>
          {VISIBILITY_FILTERS.map(f => (
            <button
              key={f.key}
              onClick={() => setVisFilter(f.key)}
              style={{
                padding: '4px 9px', borderRadius: 5, cursor: 'pointer',
                fontFamily: 'inherit', fontSize: 10, fontWeight: 700, letterSpacing: '.06em',
                textTransform: 'uppercase', transition: 'all .12s',
                background: visFilter === f.key ? 'rgba(201,168,67,.12)' : 'transparent',
                border: `1px solid ${visFilter === f.key ? 'rgba(201,168,67,.4)' : 'rgba(255,255,255,.07)'}`,
                color: visFilter === f.key ? '#C9A843' : 'rgba(200,192,168,.35)',
              }}
            >{f.label}</button>
          ))}
        </div>

        {/* Plan legend — pushed right */}
        {!isMobile && (
          <div style={{ display: 'flex', gap: 5, marginLeft: 'auto' }}>
            {PLANS.map(p => (
              <span key={p.key} style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '3px 8px', borderRadius: 20, fontSize: 9,
                fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase',
                background: p.bg, color: p.color, border: `1px solid ${p.border}`,
              }}>
                <span style={{ width: 4, height: 4, borderRadius: '50%', background: p.color }} />
                {p.label}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ── BODY — scrollable feature list + sticky preview ── */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>

        {/* Feature list */}
        <div style={{
          flex: 1, overflowY: 'auto', overflowX: 'hidden',
          /* bottom padding = save bar height so last row isn't hidden */
          padding: isMobile ? `10px 16px ${SAVE_BAR_H + 12}px` : `10px 24px ${SAVE_BAR_H + 12}px`,
        }}>
          <BulkToolbar
            selectedKeys={selectedKeys}
            onSetSelected={handleSetSelected}
            onClearSelection={() => setSelectedKeys(new Set())}
          />

          {filteredGroups.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 20px', color: 'rgba(200,192,168,.2)', fontSize: 13 }}>
              No features match your filters
            </div>
          ) : (
            filteredGroups.map(group => (
              <CategorySection
                key={group.id}
                group={group}
                config={config}
                selectedKeys={selectedKeys}
                onToggle={togglePlan}
                onSelect={handleSelect}
                onPreview={setPreviewKey}
                onBulkSet={handleBulkSet}
                visFilter={visFilter}
              />
            ))
          )}
        </div>

        {/* Preview panel — sticky, desktop only */}
        {!isMobile && (
          <div style={{
            width: 220, flexShrink: 0,
            borderLeft: '1px solid rgba(255,255,255,.06)',
            background: '#0c0c10',
            overflowY: 'auto',
            /* bottom padding = save bar height */
            padding: `12px 14px ${SAVE_BAR_H + 12}px`,
          }}>
            <PreviewPanel featureKey={previewKey} config={config} />
          </div>
        )}
      </div>

      {/* ── SAVE BAR
           Uses position:sticky at bottom of main-col — NOT fixed,
           so it never overlaps the sidebar. ── */}
      <div style={{
        flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        padding: isMobile ? '10px 16px' : '12px 24px',
        height: SAVE_BAR_H,
        background: 'rgba(8,8,10,.98)',
        borderTop: `1px solid ${isDirty ? 'rgba(201,168,67,.18)' : 'rgba(255,255,255,.06)'}`,
        backdropFilter: 'blur(16px)',
        transition: 'border-color .3s',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <span style={{
            width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
            background: isDirty ? '#C9A843' : 'rgba(200,192,168,.15)',
            boxShadow: isDirty ? '0 0 7px rgba(201,168,67,.6)' : 'none',
            transition: 'all .3s',
          }} />
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: isDirty ? 'rgba(201,168,67,.7)' : 'rgba(200,192,168,.28)' }}>
              {isDirty ? `${unsavedCount} unsaved change${unsavedCount !== 1 ? 's' : ''}` : 'All changes saved'}
            </div>
            {lastSaved && !isDirty && (
              <div style={{ fontSize: 9, color: 'rgba(200,192,168,.2)' }}>Last saved {timeAgo(lastSaved)}</div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 7 }}>
          <button onClick={handleReset} disabled={!isDirty} style={{
            padding: '7px 14px', borderRadius: 5, fontFamily: 'inherit',
            cursor: isDirty ? 'pointer' : 'not-allowed',
            background: 'transparent',
            border: `1px solid ${isDirty ? 'rgba(255,255,255,.1)' : 'rgba(255,255,255,.04)'}`,
            color: isDirty ? 'rgba(200,192,168,.5)' : 'rgba(200,192,168,.15)',
            fontSize: 11, fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase',
            transition: 'all .15s',
          }}>Discard</button>

          <button onClick={handleSave} disabled={saving || !isDirty} style={{
            padding: '7px 22px', borderRadius: 5, fontFamily: 'inherit',
            cursor: saving || !isDirty ? 'not-allowed' : 'pointer',
            background: isDirty ? 'linear-gradient(135deg,rgba(201,168,67,.22),rgba(201,168,67,.1))' : 'rgba(255,255,255,.03)',
            border: `1px solid ${isDirty ? 'rgba(201,168,67,.5)' : 'rgba(255,255,255,.05)'}`,
            color: isDirty ? '#C9A843' : 'rgba(200,192,168,.18)',
            fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase',
            transition: 'all .2s', opacity: saving ? .6 : 1,
            boxShadow: isDirty ? '0 0 18px rgba(201,168,67,.12)' : 'none',
          }}>{saving ? 'Saving…' : 'Save Configuration'}</button>
        </div>
      </div>
    </div>
  );
}